import { ActiveRecord, sources } from '@mysidewalk/active-record';
import LRU from 'lru-cache';
import ContainsCollectionQuery from './ContainsCollectionQuery.js';
import UserLayerProxy from './UserLayerProxy.js';
import populateFeatureLabels from './populateFeatureLabels.js';
const cache = new LRU(32);
/**
 * @memberof geodata
 * @classdesc
 * A GeoJSON FeatureCollection representing one or more features from
 * mySidewalk or customer sources. This collection stores their geometries
 * and by default includes their label with their properties.
 */
class FeatureCollection extends ActiveRecord {
    initialize(props) {
        super.initialize(props);
        (this.features || []).forEach((feature) => {
            feature.key = feature.id;
            feature.properties = feature.properties || {};
        });
    }
    /**
     * Create a FeatureCollection of features from the provided layer that are
     * contained by the provided FeatureReference.
     *
     * @param {feature-references.FeatureReference} constrainBy - A feature serving as the "constraint"
     * @param {feature-references.LayerReference} fromLayer - A "layer" of features to query from
     * @return {Promise<geodata.FeatureCollection>}
     */
    static fromFeatureReferenceAndLayer(constrainBy, fromLayer) {
        const key = `${fromLayer.key}-in-${constrainBy.key}`;
        const cached = cache.get(key);
        if (cached)
            return cached.then(copy);
        const feature = featureFromReference(constrainBy);
        feature.contained_feature_class = fromLayer.sourceId;
        const request = ContainsCollectionQuery.create(feature)
            .then((response) => new FeatureCollection(response))
            .then(populateFeatureLabels);
        cache.set(key, request);
        return request;
    }
    /**
     * Create a FeatureCollection of all features from the provided layer.
     *
     * @param {feature-references.LayerReference} layer
     * @return {Promise<geodata.FeatureCollection>}
     */
    static fromUserLayer(layer) {
        const key = layer.key;
        const cached = cache.get(key);
        if (cached)
            return cached.then(copy);
        const request = UserLayerProxy.retrieve(layer.sourceId)
            .then((response) => new FeatureCollection(response))
            .then(populateFeatureLabels);
        cache.set(key, request);
        return request;
    }
    /**
     * Make a geodata.FeatureCollection representing the provided feature
     * references.
     *
     * @param {feature-references.FeatureReference[]} references
     * @return {Promise<geodata.FeatureCollection>}
     */
    static fromFeatureReferences(references) {
        const key = references.map(ref => ref.key).join('+');
        const cached = cache.get(key);
        if (cached)
            return cached.then(copy);
        const features = references.map(featureFromReference);
        const props = { type: 'FeatureCollection', features };
        const request = FeatureCollection.create(props)
            .then(orderFeatures.bind(null, references))
            .then(fc => new FeatureCollection(fc))
            .then(populateFeatureLabels);
        cache.set(key, request);
        return request;
    }
    /**
     * Construct a FeatureCollection from multiple reference collections in the order provided
     *
     * @param referenceCollections [feature-references.FeatureReferenceCollection]
     * @returns {*}
     */
    static fromFeatureReferenceCollections(...referenceCollections) {
        const key = referenceCollections.map(collection => collection.hash).join('|');
        const cached = cache.get(key);
        if (cached)
            return cached.then(copy);
        const promises = referenceCollections.map(ref => ref.loadFeatureCollection());
        const request = Promise.all(promises)
            .then((featureCollections) => {
            const featureCollection = { ...featureCollections[0] };
            featureCollections.slice(1).forEach((fc) => {
                featureCollection.features = featureCollection.features.concat(fc.features);
            });
            delete featureCollection.id;
            return featureCollection;
        });
        cache.set(key, request);
        return request;
    }
}
FeatureCollection.$AR = {
    url: '',
    SourceClass: sources.ActiveRecordGeobufSource,
};
const copy = function (featureCollection) {
    return { ...featureCollection };
};
// Ensures feature reference order is retained (cached response isn't concerned with order)
function orderFeatures(featureReferences, featureCollection) {
    const indexedNewFeatures = featureCollection.features.reduce((memo, feature) => {
        memo[feature.id] = feature;
        return memo;
    }, {});
    featureCollection.features = (featureReferences
        .map(featureReference => {
        return indexedNewFeatures[featureReference.key];
    })
        .filter(v => v) // Exclude features that don't exist
    );
    return featureCollection;
}
/**
 * @private
 * @param {feature-references.FeatureReference} reference
 * @return {GeoJSON.Feature}
 */
function featureFromReference(reference) {
    return featureWithId(reference.key);
}
/**
 * @private
 * @param {string} id
 * @return {GeoJSON.Feature}
 */
function featureWithId(id) {
    return {
        id,
        key: id,
        properties: {},
        type: 'Feature',
        geometry: {
            type: 'MultiPolygon',
            coordinates: [],
        },
    };
}
export default FeatureCollection;
